import { TCString } from "@iabtcf/core/lib/TCString";
import { AlmaCmpService } from "..";
import { extendAlmaGlobal, loadScript } from "../../../utils";
import { getCookie, setCookie } from "../../../utils/cookies";
import { preloadAssets } from "../../../utils/preload";
import { getPublisherConsentsPrivate, getVendorConsentsPrivate, getNonTCFVendorConsentsPrivate, getConsentStringPrivate, } from "./alma-api";
import { AlmaB2CCmpEvent, parseConsentDataString } from "../utils";
const B2C_WRAPPER = "https://cdn.almamedia.fi/almacmp/wrapper-alma-fi.js";
const GRAVITO_DATA_COOKIE_NAME = "gravitoData";
var GravitoEvent;
(function (GravitoEvent) {
    GravitoEvent["CmpUiClosed"] = "cmpui:closed";
    GravitoEvent["CmpUiVisible"] = "cmpui:visible";
})(GravitoEvent || (GravitoEvent = {}));
var GravitoCmpUserOptedInEvent;
(function (GravitoCmpUserOptedInEvent) {
    GravitoCmpUserOptedInEvent["Layer1OptinAll"] = "layer1:opt-in:all";
    GravitoCmpUserOptedInEvent["Layer2OptinAll"] = "layer2:opt-in:all";
    GravitoCmpUserOptedInEvent["Layer2OptinSelected"] = "layer2:opt-in:selected";
    GravitoCmpUserOptedInEvent["Layer2RejectCookies"] = "layer2:reject-cookies";
    GravitoCmpUserOptedInEvent["Layer2IOptinRejectAll"] = "layer2:opt-in:reject-all";
    GravitoCmpUserOptedInEvent["OptinPreviously"] = "opt-in:previously";
})(GravitoCmpUserOptedInEvent || (GravitoCmpUserOptedInEvent = {}));
const FONTS_TO_PRELOAD = [
    {
        kind: "font",
        href: "https://cdn.almamedia.fi/fonts/GalanoGrotesqueAlt/2018-04-06/GalanoGrotesqueAlt-Regular.woff2",
        type: "font/woff2",
        crossOrigin: "anonymous",
    },
    {
        kind: "font",
        href: "https://cdn.almamedia.fi/fonts/GalanoGrotesqueAlt/2018-04-06/GalanoGrotesqueAlt-Bold.woff2",
        type: "font/woff2",
        crossOrigin: "anonymous",
    },
];
const B2C_CMP_ASSETS = [
    {
        kind: "fetch",
        href: "https://cdn.almamedia.fi/almacmp/config-fi.js",
        type: "application/x-javascript",
        crossOrigin: "anonymous",
    },
    {
        kind: "fetch",
        href: "https://cdn.almamedia.fi/almacmp/vendor-list.json",
        type: "application/json",
        crossOrigin: "anonymous",
    },
    {
        kind: "fetch",
        href: "https://cdn.almamedia.fi/almacmp/purposes-fi.json",
        type: "application/json",
        crossOrigin: "anonymous",
    },
    {
        kind: "script",
        href: "https://cdn.almamedia.fi/almacmp/uibundle.js",
        type: "application/x-javascript",
    },
    {
        kind: "script",
        href: "https://cdn.almamedia.fi/almacmp/bundle.js",
        type: "application/x-javascript",
    },
    {
        kind: "script",
        href: B2C_WRAPPER,
        type: "application/x-javascript",
    },
];
class AlmaB2CCmpService extends AlmaCmpService {
    constructor(config, context) {
        super({ cmpScript: config.cmpScript ?? B2C_WRAPPER }, context);
        this.consentKind = "unknown";
        this.logger.logInfo(`Preloading B2C CMP assets`);
        preloadAssets([...FONTS_TO_PRELOAD, ...B2C_CMP_ASSETS]);
        extendAlmaGlobal({
            getPublisherConsents: this.getPublisherConsents.bind(this),
            getVendorConsents: this.getVendorConsents.bind(this),
            getNonTCFVendorConsents: this.getNonTCFVendorConsents.bind(this),
            getConsentString: this.getConsentString.bind(this),
            optin: false,
        }, "cmpV2");
    }
    async passExternalConsent(sharedConsentDataString) {
        this.logger.logInfo(`Received external consent ${sharedConsentDataString}`);
        let sharedConsentDataObject;
        if (sharedConsentDataString) {
            try {
                sharedConsentDataObject = JSON.parse(sharedConsentDataString);
            }
            catch (e) {
                this.logger.logError(`Could not parse shared consent object: ${e}`);
            }
        }
        const localConsentDataString = await getCookie(GRAVITO_DATA_COOKIE_NAME);
        // Validate external profile
        const sharedConsent = parseConsentDataString(sharedConsentDataString);
        // validate local profile
        const localConsent = parseConsentDataString(localConsentDataString);
        if (sharedConsent && !localConsent) {
            this.logger.logWarn("Setting shared consent as local consent");
            this.consentKind = "sharedAsLocal";
            const expires = new Date();
            expires.setTime(expires.getTime() + 60 * 60 * 24 * 365 * 1000);
            await setCookie("TcString", sharedConsentDataObject?.TCString, expires);
            await setCookie("gravitoData", sharedConsentDataString, expires);
        }
        else if (sharedConsent && localConsent) {
            if (sharedConsent.lastUpdated.getTime() ===
                localConsent.lastUpdated.getTime()) {
                this.logger.logInfo(`Shared and local consent dates are equal`);
                this.consentKind = "bothEqual";
            }
            else if (sharedConsent.lastUpdated.getTime() < localConsent.lastUpdated.getTime()) {
                this.logger.logInfo(`Local consent is newer, share it to Gravito`);
                this.shareLocalConsent(localConsentDataString);
                this.consentKind = "localAsShared";
            }
            else if (sharedConsent.lastUpdated.getTime() > localConsent.lastUpdated.getTime()) {
                this.logger.logWarn("Shared consent is newer");
                this.logger.logWarn("Setting shared consent as local consent");
                this.consentKind = "sharedAsLocal";
                const expires = new Date();
                expires.setTime(expires.getTime() + 60 * 60 * 24 * 365 * 1000);
                await setCookie("TcString", sharedConsentDataObject?.TCString, expires);
                await setCookie("gravitoData", sharedConsentDataString, expires);
            }
        }
        await this.loadCmpWrapper(this.configuration.cmpScript);
    }
    shareLocalConsent(consent) {
        this.logger.logInfo(`Tagging local consent as observation`);
        this.context.services.gravitoService?.tagObservation({
            event: "consent",
            gravitoData: consent,
        });
    }
    async handleGravitoEvent(event) {
        this.logger.logInfo(`Received event from Gravito-CMP: ${JSON.stringify(event.detail, null, 2)}`);
        if (event.detail.eventType === GravitoEvent.CmpUiVisible) {
            // CMP UI was shown - user had no shared/local consent or consent was outdated (or user opened it manually)
            if (!this.cmpReleased) {
                this.context.core?.recordEvent("cmp.user-not-consented", window.performance.now());
            }
        }
        if (event.detail.eventType === GravitoEvent.CmpUiClosed) {
            const local = await getCookie(GRAVITO_DATA_COOKIE_NAME);
            if (local) {
                this.logger.logInfo(`Store local consent to Gravito`);
                this.shareLocalConsent(local);
            }
        }
        // This has to refire if user changes their consent. We don't actually check if it has changed, only that user closed CMP with a button that might change it.
        if (Object.values(GravitoCmpUserOptedInEvent).includes(event.detail.eventType) &&
            this.cmpReleased) {
            const consent = await this.getPublisherConsents();
            this.fireCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2HasOptedin, () => consent);
        }
        if (event.detail.eventType === "cmploaded") {
            this.context.core?.recordEvent("cmp.scripts.end", window.performance.now());
            this.fireCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2Available);
        }
        if (Object.values(GravitoCmpUserOptedInEvent).includes(event.detail.eventType) &&
            !this.cmpReleased) {
            this.context.core?.recordEvent("cmp.consent-available", window.performance.now(), {
                consent_kind: this.consentKind,
            });
            this.logger.logInfo(`User has optedin, release queue`);
            this.cmpReleased = true;
            extendAlmaGlobal({ ...window.ALMA.cmpV2, optin: true }, "cmpV2");
            this.requestsQueue.forEach((r) => r.resolve());
            try {
                const consents = await this.getPublisherConsents();
                if (event.detail.eventType === GravitoCmpUserOptedInEvent.OptinPreviously) {
                    this.fireCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2OptinDone, () => consents);
                }
                if (event.detail.eventType ===
                    GravitoCmpUserOptedInEvent.Layer1OptinAll ||
                    event.detail.eventType ===
                        GravitoCmpUserOptedInEvent.Layer2OptinAll ||
                    event.detail.eventType ===
                        GravitoCmpUserOptedInEvent.Layer2OptinSelected ||
                    event.detail.eventType ===
                        GravitoCmpUserOptedInEvent.Layer2RejectCookies ||
                    event.detail.eventType ===
                        GravitoCmpUserOptedInEvent.Layer2IOptinRejectAll) {
                    this.fireCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2HasOptedin, () => consents);
                }
                this.fireCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2ConsentAvailable, () => consents);
                this.repetitiveEventInterval = this.startCmpEvent(AlmaB2CCmpEvent.AlmaCmpV2ConsentAvailableRepetitive, this.getPublisherConsents.bind(this), 500);
            }
            catch (e) {
                this.logger.logInfo(`Error occured when processing Gravito state: ${e}`);
            }
        }
    }
    async loadCmpWrapper(cmpScript) {
        if (!this.cmpReleased) {
            this.logger.logInfo(`Loading CMP wrapper from ${cmpScript}`);
            this.context.core?.recordEvent("cmp.scripts.start", window.performance.now());
            window.addEventListener("gravito:tcfv2:client", this.handleGravitoEvent.bind(this), false);
            await loadScript(cmpScript, window);
        }
        else {
            this.logger.logWarn(`CMP wrapper already loaded`);
        }
    }
    async fireCmpEvent(event, resolveDetail, silent = false) {
        try {
            const payload = resolveDetail ? await resolveDetail() : undefined;
            const eventObject = new CustomEvent(event, {
                detail: payload,
            });
            if (!silent) {
                this.logger.logDebug(`Firing CMP event ${event} with payload ${JSON.stringify(payload, null, 2)}`);
            }
            window.dispatchEvent(eventObject);
        }
        catch (e) {
            this.logger.logError(`Error while dispatching event ${event}: ${e}`);
            if (event === AlmaB2CCmpEvent.AlmaCmpV2ConsentAvailableRepetitive) {
                clearInterval(this.repetitiveEventInterval);
            }
        }
    }
    startCmpEvent(event, resolveDetail, eventInterval) {
        this.logger.logInfo(`Starting CMP event emitter ${event}`);
        return window.setInterval(() => this.fireCmpEvent(event, resolveDetail, true), eventInterval);
    }
    async getPublisherConsents(callback) {
        return this.wrapInternalApi(getPublisherConsentsPrivate, callback);
    }
    getVendorConsents(arg0, arg1) {
        return this.wrapInternalApi(async () => {
            const filterParam = arg1 ?? arg0;
            if (typeof filterParam === "object" && filterParam.type) {
                const consents = getVendorConsentsPrivate();
                if (filterParam.type === "consented") {
                    const filtered = Object.entries(consents).reduce((arr, curr) => {
                        if (curr[1] === true) {
                            return [...arr, Number.parseInt(curr[0], 10)];
                        }
                        return arr;
                    }, []);
                    return filtered;
                }
                if (filterParam.type === "both") {
                    const filtered = {
                        true: Object.entries(consents).reduce((arr, curr) => {
                            if (curr[1] === true) {
                                return [...arr, Number.parseInt(curr[0], 10)];
                            }
                            return arr;
                        }, []),
                        false: Object.entries(consents).reduce((arr, curr) => {
                            if (curr[1] === false) {
                                return [...arr, Number.parseInt(curr[0], 10)];
                            }
                            return arr;
                        }, []),
                    };
                    return filtered;
                }
            }
            return getVendorConsentsPrivate();
        }, arg0);
    }
    getNonTCFVendorConsents(arg0, arg1) {
        return this.wrapInternalApi(async () => {
            const filterParam = arg1 ?? arg0;
            if (typeof filterParam === "object" && filterParam.type) {
                const consents = getNonTCFVendorConsentsPrivate();
                if (filterParam.type === "consented") {
                    const filtered = Object.entries(consents).reduce((arr, curr) => {
                        if (curr[1] === true) {
                            return [...arr, Number.parseInt(curr[0], 10)];
                        }
                        return arr;
                    }, []);
                    return filtered;
                }
                if (filterParam.type === "both") {
                    const filtered = {
                        true: Object.entries(consents).reduce((arr, curr) => {
                            if (curr[1] === true) {
                                return [...arr, Number.parseInt(curr[0], 10)];
                            }
                            return arr;
                        }, []),
                        false: Object.entries(consents).reduce((arr, curr) => {
                            if (curr[1] === false) {
                                return [...arr, Number.parseInt(curr[0], 10)];
                            }
                            return arr;
                        }, []),
                    };
                    return filtered;
                }
            }
            return getNonTCFVendorConsentsPrivate();
        }, arg0);
    }
    async getConsentString(callback) {
        return this.wrapInternalApi(getConsentStringPrivate, callback);
    }
    parseConsentDataString(consentDataString) {
        this.logger.logInfo(`Trying to parse consent data string as JSON`);
        if (!consentDataString) {
            this.logger.logInfo(`Consent string is empty`);
            return undefined;
        }
        let consentDataObject;
        try {
            consentDataObject = JSON.parse(consentDataString);
        }
        catch (e) {
            this.logger.logError(`Unable to parse consent data string: ${e}`);
            return undefined;
        }
        this.logger.logInfo(`Decoding consent data object ${JSON.stringify(consentDataObject, null, 2)}`);
        try {
            const decodedConsent = TCString.decode(consentDataObject.TCString);
            return decodedConsent;
        }
        catch (e) {
            this.logger.logError(`Unable to decode consent string: ${e}`);
            return undefined;
        }
    }
}
export default AlmaB2CCmpService;
//# sourceMappingURL=alma-b2c-cmp.js.map